import { computed } from 'vue'
import { useStore } from 'vuex'

/*
 * The token balance as the app shell shows it — the pill in the top bar and the
 * row in the sidebar footer — so both follow ONE rule.
 *
 * They used to disagree: the top bar said green/red, the sidebar grey/amber, and
 * in both the colour alone carried the meaning. Now it is neutral while funded,
 * and amber PLUS the words "Top up" when low.
 *
 * "Low" is one token or fewer: `tokens > 1` is the test both surfaces already
 * used for "funded", kept exactly so nothing flips state for anyone.
 */
export const LOW_BALANCE_MAX = 1

/* The compact figure is what lets the balance fit a phone's top bar, so it is
   built in a FIXED locale with at most three significant digits: never more
   than five characters ("123K", "12.3K", "1.25M"). In the reader's own locale
   it could triple — ru-RU writes 123456 as "123,5 тыс." — and that pushed the
   account button, and Log out with it, off a 360px screen. The exact figure,
   localised, is in the pill's label and on every wider screen. */
const compactFormat = (() => {
  try {
    return new Intl.NumberFormat('en', { notation: 'compact', maximumSignificantDigits: 3 })
  } catch (e) {
    return null
  }
})()

// The same shape by hand, for an engine without compact notation.
const compactByHand = (n) => {
  for (const [size, suffix] of [[1e9, 'B'], [1e6, 'M'], [1e3, 'K']]) {
    if (Math.abs(n) >= size) return `${Number((n / size).toPrecision(3))}${suffix}`
  }
  return String(Number(n.toPrecision(3)))
}

export function useTokenBalance () {
  const store = useStore()

  const tokens = computed(() => store?.state?.auth?.user?.user?.tokens)

  const isLow = computed(() => (Number(tokens.value) || 0) <= LOW_BALANCE_MAX)

  /** The exact figure. Whatever the API sent, if it was not a number. */
  const full = computed(() =>
    typeof tokens.value === 'number' ? tokens.value.toLocaleString() : tokens.value ?? 0
  )

  /** Five characters at most; the exact figure belongs in a label. */
  const compact = computed(() => {
    const n = Number(tokens.value)
    if (tokens.value === null || tokens.value === undefined || !Number.isFinite(n)) return full.value
    return compactFormat ? compactFormat.format(n) : compactByHand(n)
  })

  return { tokens, isLow, full, compact }
}
